import * as React from "react";
import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Link } from "react-router-dom";
import ShiftTimeLine from "./ShiftTimeLine";
import { Avatar, Button, Chip, TextField, Typography, useTheme } from "@mui/material";
import { useEffect } from "react";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import TimelapseOutlined from "@mui/icons-material/TimelapseOutlined";
import { CalendarIcon } from "@mui/x-date-pickers";
import Tooltip from "@mui/material/Tooltip";

export default function ViewOnlyShiftBox(props) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const fullData = { employeeDetail: props.employee, email: props.email };
    const { schedule } = props.shift;
    const [startvalue, setStartValue] = useState(new Date(props.shift.startDate));
    const [endvalue, setEndValue] = useState(
        !props.shift.endDate ? new Date() : new Date(props.shift.endDate)
    );
    const [breakTime, setBrakeTime] = useState(
        Array.isArray(props.shift.break) ? props.shift.break : []
    );
    const [shiftApproved, setShiftApproved] = useState(props.shift.approved);
    const [shiftLength, setShiftLength] = useState(props.shift.totalShiftLength);
    const [totalBreak, setTotalBreak] = useState(props.shift.totalBreak);
    const [shiftWOBreak, setShiftWOBreak] = useState(
        props.shift.shiftWithoutBreak
    );

    // Helper function to format time as HH:MM
    function formatTimeHHMM(timeString) {
        if (!timeString) return "00:00";

        // If it's a Date object
        if (timeString instanceof Date) {
            const hours = timeString.getHours().toString().padStart(2, '0');
            const minutes = timeString.getMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes}`;
        }
        if (typeof timeString === 'string' && timeString.includes(':')) {
            return timeString.substring(0, 5);
        }
        return "00:00";
    }

    function convertS3ToCloudFront(url) {
        // Replace the AWS S3 domain with the CloudFront domain
        if (!url) return "";
        const cloudFrontDomain = 'https://d2k8ghk99lpcqm.cloudfront.net/';
        const s3Domain = 'https://cms-shifts.s3.ap-southeast-2.amazonaws.com/';
        
        // Check if the URL starts with the S3 domain
        if (url && url.startsWith(s3Domain)) {
          // Replace the S3 domain with the CloudFront domain
          const convertedUrl = url.replace(s3Domain, cloudFrontDomain);
          return convertedUrl;
        } else {
          // URL doesn't match the expected pattern
          return url;
        }
    }

    // Calculate time difference in HH:MM format ignoring seconds
    function calculateTimeDifferenceHHMM(startTime, endTime) {
        let start = new Date(startTime);
        let end = new Date(endTime || new Date());

        // Set seconds and milliseconds to zero to ignore them
        start.setSeconds(0, 0);
        end.setSeconds(0, 0);

        let diff = Math.abs(end - start);

        let hours = Math.floor(diff / 3.6e6);
        let minutes = Math.floor((diff % 3.6e6) / 6e4);

        // Format as HH:MM
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    function calculateTotalBreak() {
        let totalMinutes = 0;

        breakTime.forEach((brk) => {
            let start = new Date(brk.start);
            let end = new Date(brk.end || new Date());

            // Set seconds to 0 to ignore them
            start.setSeconds(0, 0);
            end.setSeconds(0, 0);

            let diff = Math.abs(end - start);
            totalMinutes += Math.floor(diff / 60000); // Convert ms to minutes
        });

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const total = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
        setTotalBreak(total);
    }

    function calculateTotalShiftLength() {
        let start = new Date(startvalue);
        let end = new Date(endvalue);

        // Set seconds to 0 to ignore them
        start.setSeconds(0, 0);
        end.setSeconds(0, 0);

        let diff = Math.abs(end - start);

        let hours = Math.floor(diff / 3.6e6);
        let minutes = Math.floor((diff % 3.6e6) / 6e4);

        const total = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
        setShiftLength(total);
    }

    const calculateshiftWithoutBreak = () => {
        // Parse times from HH:MM:SS format
        const [breakHours, breakMinutes] = totalBreak.split(':').map(Number);
        const [shiftHours, shiftMinutes] = shiftLength.split(':').map(Number);

        // Calculate total minutes
        const totalBreakMinutes = breakHours * 60 + breakMinutes;
        const totalShiftMinutes = shiftHours * 60 + shiftMinutes;

        // Calculate shift without break in minutes
        let remainingMinutes = totalShiftMinutes - totalBreakMinutes;

        // Handle negative case (if break > shift)
        if (remainingMinutes < 0) remainingMinutes = 0;

        // Convert back to hours and minutes
        const hours = Math.floor(remainingMinutes / 60);
        const minutes = remainingMinutes % 60;

        // Format result
        const total = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
        setShiftWOBreak(total);
    };

    useEffect(() => {
        calculateTotalBreak();
        calculateTotalShiftLength();
        calculateshiftWithoutBreak();
    }, [
        startvalue,
        endvalue,
        breakTime,
        totalBreak,
        shiftWOBreak,
        shiftLength,
    ]);

    const getAvatarName = (employee) => {
        if (employee.employeeDetail) {
            if (employee.employeeDetail.fname && employee.employeeDetail.lname) {
                return employee.employeeDetail.fname.charAt(0).toUpperCase() + employee.employeeDetail.lname.charAt(0).toUpperCase();
            }
            else if (employee.employeeDetail.fname) {
                return employee.employeeDetail.fname.charAt(0).toUpperCase();
            }
            else if (employee.employeeDetail.lname) {
                return employee.employeeDetail.lname.charAt(0).toUpperCase();
            }
        }
        else {
            return employee.email.charAt(0).toUpperCase();
        }
    }

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
    };

    const {
        startImage,
        endImage,
        startDate,
        endDate,
        shiftWithoutBreak,
        totalShiftLength,
        approved
    } = props.shift;

    return (
        <>
            <Box sx={{ border: `2px solid ${shiftApproved ? theme.palette.success.main : theme.palette.info.main}`, width: "180px", paddingBottom: "10px", borderRadius: "20px", display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "center", textAlign: "center", cursor: "pointer", backgroundColor: theme.palette.background.paper, transition: "background-color 0.3s ease", margin: "auto", marginTop: "5px" }} onClick={handleClickOpen}>
                <Box sx={{
                    fontWeight: "500",
                    backgroundColor: shiftApproved ? theme.palette.success.main : theme.palette.info.main,
                    color: theme.palette.text.secondary, borderRadius: "10px", width: "90%", margin: "10px 2px 2px 2px", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center"
                }}>
                    <Box sx={{ padding: "2px 0" }}>
                        {formatTime(startDate).slice(0, 5)} -{" "}
                        {endDate ? formatTime(endDate).slice(0, 5)
                            :
                            <span>Active</span>}
                    </Box>
                </Box>
                <Box sx={{
                    display: "flex", flexDirection: "row",
                    justifyContent: "space-between", alignItems: "center",
                    textAlign: "center",
                    fontSize: "1rem",
                    padding: "0",
                    fontWeight: "300",
                }}>
                    <Box sx={{ marginTop: "5px" }}>
                        Shift:{" "} <span>{formatTimeHHMM(shiftLength)}</span>
                    </Box>
                    <Box sx={{ marginRight: "5px", marginLeft: "5px" }}>
                        |
                    </Box>
                    <Box sx={{ marginTop: "5px" }}>
                        Break: <span>{formatTimeHHMM(totalBreak)}</span>
                    </Box>
                </Box>
                <Box sx={{ fontSize: "1rem", fontWeight: "300", marginTop: "5px" }} >
                    Shift w/o break:{" "}
                    <span
                        style={{ color: "#ee5622", fontWeight: "600" }}
                    >{formatTimeHHMM(shiftWOBreak)} </span>
                </Box>
            </Box>
            
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                  style: {
                    minWidth: "600px",
                    overflowY: "auto",
                    width: "max-content",
                    padding: "0",
                  },
                }}
            >
                <DialogContent style={{ display: "flex", padding: "0" }}>
                    <div
                        style={{
                            padding: "20px",
                            backgroundColor: theme.palette.background.default,
                            display: "flex",
                            flexDirection: "column",
                            flex: "1",
                            borderRadius: "20px",
                            position: "sticky",
                            top: "0",
                            zIndex: "100",
                            minWidth: "150px",
                            color: theme.palette.text.primary,
                        }}>
                        <div style={{ margin: "0 auto" }}>
                            <Avatar style={{
                                width: "5rem", height: "5rem", color: "#fff", fontWeight: "500", fontSize: "2rem",
                                borderRadius: "20%"
                            }}
                            >
                                {getAvatarName(fullData)}
                            </Avatar>
                        </div>
                        <Link
                            style={{ textDecoration: "none", color: theme.palette.text.primary, margin: "0 auto" }}
                            to={`/employee/${props.employeeId}`}
                        >
                            <div>
                                <Typography style={{ fontWeight: "500", textAlign: "center", fontSize: "1.5rem", textTransform: "capitalize", cursor: "pointer", margin: "0 auto" }}>
                                    {props.employee.fname} {props.employee.lname}
                                </Typography>
                            </div>
                        </Link>

                        <Grid container spacing={1} style={{ marginTop: "10px" }}>
                            <Grid item xs={3} md={3}>
                                <AccessTimeIcon />
                            </Grid>
                            <Grid item xs={9} md={9}>
                                <span style={{ fontWeight: "600", textAlign: "center" }}>
                                    {shiftLength.slice(0, 5)}
                                </span>
                                <br />
                                <span style={{ fontSize: "0.8rem", textAlign: "center" }}>
                                    Shift length
                                </span>
                                <br />
                            </Grid>
                        </Grid>
                        
                        <Grid container spacing={1} style={{ marginTop: "10px" }}>
                            <Grid item xs={3} md={3}>
                                <FormatListBulletedIcon />
                            </Grid>
                            <Grid item xs={9} md={9}>
                                <span style={{ fontWeight: "600", textAlign: "center" }}>
                                    {totalBreak.slice(0, 5)}
                                </span>
                                <br />
                                <span style={{ fontSize: "0.8rem", textAlign: "center" }}>
                                    Total Break
                                </span>
                                <br />
                            </Grid>
                        </Grid>
                        
                        <Grid container spacing={1} style={{ marginTop: "10px" }}>
                            <Grid item xs={3} md={3}>
                                <TimelapseOutlined />
                            </Grid>
                            <Grid item xs={9} md={9}>
                                <span style={{ fontWeight: "600", textAlign: "center", color: "#ee5622" }}>
                                    {shiftWOBreak.slice(0, 5)}
                                </span>
                                <br />
                                <span style={{ fontSize: "0.8rem", textAlign: "center" }}>
                                    Shift w/o break
                                </span>
                                <br />
                            </Grid>
                        </Grid>
                        
                        <div style={{ marginTop: "15px" }}>
                            {/* {schedule && (
                                <Tooltip
                                    title={<div>{scheduleContent}</div>}
                                    arrow
                                    placement="top"
                                    PopperProps={{
                                        modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                            offset: [0, 8],
                                            },
                                        },
                                        ],
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        style={{ 
                                        margin: "0 auto", 
                                        display: "block", 
                                        backgroundColor: theme.palette.info.main,
                                        color: theme.palette.text.secondary,
                                        textTransform: "none",
                                        fontWeight: "500",
                                        fontSize: "1rem",
                                        padding: "5px 10px",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        display: "flex"
                                        }}
                                    >
                                        <CalendarIcon sx={{marginRight: "2px", fontSize: "1rem"}} />
                                        Schedule
                                    </Button>
                                </Tooltip>
                            )} */}
                        </div>

                        <Typography style={{ position: "absolute", left: "15px", bottom: "20px", fontWeight: "600", fontSize: "1rem", marginRight: "5px" }}>
                            Shift ID: #{props.shift.id}
                        </Typography>
                    </div>

                    <div style={{ padding: "20px", fontSize: "20px" }}>
                        <Typography variant="h5"><strong>Shift Details - {new Date(props.shift.startDate).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    })}</strong></Typography>
                        {!props.shift.endDate && <Typography style={{ color: "#ee5622", fontWeight: "500" }}>Currently Active</Typography>}
                        
                        <Grid container spacing={2} style={{ marginTop: "10px" }}>
                            <Grid item xs={6}>
                                <Typography variant="body1" style={{ fontWeight: "600" }}>
                                    Start Time: <span style={{ backgroundColor: "#ee5622", color: "white", fontWeight: "600", padding: "2px 5px", borderRadius: "5px" }}>
                                        {formatTime(props.shift.startDate).slice(0, 5)}
                                    </span>
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body1" style={{ fontWeight: "600" }}>
                                    End Time: <span style={{ backgroundColor: "#ee5622", color: "white", fontWeight: "600", padding: "2px 5px", borderRadius: "5px" }}>
                                        {props.shift.endDate ? formatTime(props.shift.endDate).slice(0, 5) : "Active"}
                                    </span>
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <img
                                    src={startImage && convertS3ToCloudFront(startImage)}
                                    alt="Start Shift Image"
                                    style={{
                                        width: "100%",
                                        borderRadius: "10px",
                                        border: `1px solid ${theme.palette.divider}`
                                    }}
                                />
                                <Typography variant="caption" style={{ display: "block", textAlign: "center", marginTop: "4px" }}>
                                    Start Image
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <img
                                    src={endImage && convertS3ToCloudFront(endImage)}
                                    alt="End Shift Image"
                                    style={{
                                        width: "100%",
                                        borderRadius: "10px",
                                        border: `1px solid ${theme.palette.divider}`
                                    }}
                                />
                                <Typography variant="caption" style={{ display: "block", textAlign: "center", marginTop: "4px" }}>
                                    End Image
                                </Typography>
                            </Grid>
                        </Grid>

                        <div style={{ marginTop: "0.5rem" }}>
                            <Typography variant="subtitle1" style={{ fontWeight: "500", marginTop: "10px" }}>
                                Breaks
                            </Typography>
                            {!breakTime || breakTime.length === 0 ? (
                                <Typography style={{ fontStyle: "italic", color: theme.palette.text.primary }}>
                                    No breaks recorded
                                </Typography>
                            ) : (
                                breakTime.map((brk, index) => (
                                    <Grid container spacing={2} key={index} style={{ marginTop: "8px" }}>
                                        <Grid item xs={10}>
                                            <Typography variant="body1" >
                                                Break {index + 1}: <span style={{ backgroundColor: "#ee5622", color: "white", fontWeight: "600", padding: "2px 5px", borderRadius: "5px" }}>
                                                    {formatTime(brk.start).slice(0, 5) + " to " + formatTime(brk.end).slice(0, 5) + " = " + calculateTimeDifferenceHHMM(brk.start, brk.end)}
                                                </span>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                ))
                            )}
                        </div>

                        <div style={{ marginTop: "1rem" }}>
                            <ShiftTimeLine shiftTimelines={props.shift.shiftTimelines} />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
